/**
 * The skip path on the install step (Paper "Onboarding · 04 Install"): the
 * waiting line plus an outline "Skip, install later". Shown once verification
 * has failed enough times that skipping is a fair offer (see
 * `InstallScriptPanel`), never as the first thing on the step.
 */

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/** The waiting line. Names the drafted tests only when the caller has them. */
export function skipNoticeCopy(draftedTestCount?: number): string {
  const lead = "Waiting for your first visitor. Open your site once the tag is published."
  if (typeof draftedTestCount === "number" && draftedTestCount > 0) {
    const tests = `${draftedTestCount} test${draftedTestCount === 1 ? "" : "s"}`
    return `${lead} Skipping keeps your ${tests} drafted; numbers start when the tag goes live.`
  }
  return `${lead} You can skip for now; numbers start when the tag goes live.`
}

export function InstallSkipNotice({
  draftedTestCount,
  onSkip,
  className,
}: {
  draftedTestCount?: number
  onSkip: () => void
  className?: string
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl bg-muted/60 px-4 py-3.5 shadow-[0_0_0_1px_var(--border)] sm:flex-row sm:items-center",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
        <span
          aria-hidden
          className="mt-1 size-2 shrink-0 rounded-full bg-live shadow-[0_0_0_4px_color-mix(in_oklab,var(--live)_20%,transparent)] sm:mt-0"
        />
        <p className="min-w-0 text-[13px] leading-4 text-ui-ink">{skipNoticeCopy(draftedTestCount)}</p>
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={onSkip}
        className="h-8 shrink-0 self-start rounded-lg px-3.5 text-[13px] font-medium sm:self-auto"
      >
        Skip, install later
      </Button>
    </div>
  )
}
